/**
 * HTTP client for the Concierge Builder API.
 *
 * Adapted from admin-dashboard/src/services/concierge-api-client.ts.
 * Uses the Studio auth token (Auth0 JWT) for all requests.
 */

import axios, { AxiosInstance } from 'axios';
import type { EventSiteData, ScrapePreviewResponse, ExtractFileResponse } from '../types/event-data';
import type { BookingExtractResponse, ImportBookingRequest } from '../types/groups';
import { putToSignedUrl } from '../lib/signedUpload';
import {
  placeLookupFailure,
  placeLookupResult,
  type PlaceLookupResult,
} from '../lib/placeLookup';

export const CONCIERGE_API_URL =
  import.meta.env.VITE_CONCIERGE_BUILDER_API_URL ||
  'https://concierge-builder-api-55189446448.us-central1.run.app';

let authToken: string | null = null;

const client: AxiosInstance = axios.create({
  baseURL: CONCIERGE_API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

export const conciergeApi = {
  /**
   * Uploads a group's hero image and returns the public URL to store on the group.
   *
   * Two steps by design: the builder API signs a short-lived PUT (after checking
   * with the orchestrator that this caller owns the group), then the bytes go
   * straight from the browser to GCS. The image never passes through our API, so
   * a large photo on a hotel wifi does not tie up a request thread.
   */
  async uploadGroupHeroImage(groupId: string, file: File): Promise<string> {
    const { data } = await client.post(`/groups/${groupId}/hero-image/upload-url`, {
      mime_type: file.type,
      file_size_bytes: file.size,
    });

    // Send the headers the API signed, verbatim. They are part of the signature,
    // so a client-side copy of the size limit that drifts from the server's turns
    // every upload into a 403.
    await putToSignedUrl(data, file);
    return data.public_url as string;
  },

  setAuthToken(token: string) {
    authToken = token;
  },

  clearAuthToken() {
    authToken = null;
  },

  // --- Concierge instances ---

  async listConcierges() {
    const { data } = await client.get('/concierges');
    return data;
  },

  async getConcierge(instanceId: string) {
    const { data } = await client.get(`/concierges/${instanceId}`);
    return data;
  },

  async createConcierge(body: {
    name: string;
    description?: string;
    use_case?: string;
    event_date?: string;
    config?: Record<string, unknown>;
  }) {
    const { data } = await client.post('/concierges', body);
    return data;
  },

  async updateConfig(instanceId: string, config: Record<string, any>) {
    const { data } = await client.patch(`/concierges/${instanceId}/config`, { config });
    return data;
  },

  async deleteConcierge(instanceId: string) {
    const { data } = await client.delete(`/concierges/${instanceId}`);
    return data;
  },

  // Provision a dedicated SMS number (Bird) for the concierge.
  async provisionConcierge(instanceId: string): Promise<{
    instance_id: string;
    phone_number: string;
    status: string;
  }> {
    const { data } = await client.post(
      `/concierges/${instanceId}/provision`,
      {},
      { timeout: 60000 }
    );
    return data;
  },

  // --- Uploads ---

  async getUploadUrl(
    instanceId: string,
    body: { filename: string; mime_type: string; file_size_bytes?: number }
  ) {
    const { data } = await client.post(`/concierges/${instanceId}/uploads/url`, body);
    return data;
  },

  async listUploads(instanceId: string) {
    const { data } = await client.get(`/concierges/${instanceId}/uploads`);
    return data;
  },

  async deleteUpload(instanceId: string, uploadId: string) {
    const { data } = await client.delete(`/concierges/${instanceId}/uploads/${uploadId}`);
    return data;
  },

  // --- Magic Create extraction ---

  async scrapePreview(url: string, eventType: string): Promise<ScrapePreviewResponse> {
    const { data } = await client.post('/concierges/scrape-preview', {
      url,
      event_type: eventType,
    }, { timeout: 150000 }); // 150s — Zola/SPA sites can take 40s+ to render, plus subpage fetches + Gemini
    return data;
  },

  async extractFile(fileBase64: string, mimeType: string, eventType: string): Promise<ExtractFileResponse> {
    const { data } = await client.post('/concierges/extract-file', {
      file_base64: fileBase64,
      mime_type: mimeType,
      event_type: eventType,
    }, { timeout: 120000 });
    return data;
  },

  async getExtraction(extractionId: string) {
    const { data } = await client.get(`/concierges/extractions/${extractionId}`);
    return data;
  },

  // --- Booking ingestion (Tripsby Together) ---
  // Extract-only: parse a forwarded hotel confirmation for host review. Never
  // commits — the host confirms in Studio and the reservation is created via
  // the OrchestratorAPI host-assign path (groupsApi.assignReservation).
  async importBooking(body: ImportBookingRequest): Promise<BookingExtractResponse> {
    const { data } = await client.post('/groups/bookings/extract', body, { timeout: 120000 });
    return data;
  },

  // --- Authenticated import (host logs in to a login-gated source, we capture) ---

  async startAuthImport(url: string, eventType = 'event') {
    const { data } = await client.post('/groups/import/authenticated/start', {
      url,
      event_type: eventType,
    });
    return data as { import_id: string; status: string; live_view_url: string; provider?: string };
  },

  async captureAuthImport(importId: string, eventType = 'event') {
    const { data } = await client.post(
      `/groups/import/authenticated/${importId}/capture`,
      { event_type: eventType },
      { timeout: 120000 },
    );
    return data as {
      import_id: string;
      status: string;
      provider?: string;
      event_data?: Record<string, any> | null;
      guests?: Array<{ name: string; rsvp_status?: string | null }>;
      error?: string | null;
    };
  },

  async closeAuthImport(importId: string) {
    const { data } = await client.delete(`/groups/import/authenticated/${importId}`);
    return data;
  },

  /**
   * Resolve a typed venue name to a canonical place.
   *
   * Returns an outcome rather than a nullable place: a lookup that could not run
   * must not read as "there is no such venue". Classification lives in
   * lib/placeLookup so it can be tested without a transport.
   */
  async resolveGroupPlace(
    groupId: string,
    body: { query: string; city?: string; country?: string },
  ): Promise<PlaceLookupResult> {
    try {
      const { data } = await client.post(`/groups/${groupId}/places/resolve`, body);
      return placeLookupResult(data);
    } catch (error) {
      return placeLookupFailure(error);
    }
  },
};
